// Copyright 2025, compose-coui-ui contributors
// SPDX-License-Identifier: Apache-2.0
package io.github.suqi8.coui.kmp.basic

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithCache
import androidx.compose.ui.draw.paint
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.ColorProducer
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.layer.drawLayer
import androidx.compose.ui.graphics.painter.BitmapPainter
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.toolingGraphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import io.github.suqi8.coui.kmp.theme.LocalContentColor

/**
 * A [Icon] component with COUI style.
 *
 * Draws [imageVector] using [tint], with a default value of [LocalContentColor]. If [imageVector]
 * has no intrinsic size, this component falls back to the recommended default size. Icon is an
 * opinionated component designed to be used with single-color icons so that they can be tinted
 * correctly for the component they are placed in. For multicolored icons and icons that should not
 * be tinted, use [Color.Unspecified] for [tint]. For generic images that should not be tinted, and
 * do not follow the recommended icon size, use [androidx.compose.foundation.Image] instead. For a
 * clickable icon, see [IconButton].
 *
 * @param imageVector [ImageVector] to draw inside this icon
 * @param contentDescription text used by accessibility services to describe what this icon
 *   represents. This should always be provided unless this icon is used for decorative purposes,
 *   and does not represent a meaningful action that a user can take. This text should be localized,
 *   such as by using [stringResource] or similar
 * @param modifier the [Modifier] to be applied to this icon
 * @param tint tint to be applied to [imageVector]. If [Color.Unspecified] is provided, then no tint
 *   is applied.
 */
@Composable
fun Icon(
    imageVector: ImageVector,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    tint: Color = LocalContentColor.current,
) {
    Icon(
        painter = rememberVectorPainter(imageVector),
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
    )
}

/**
 * A [Icon] component with COUI style.
 *
 * Draws [bitmap] using [tint], with a default value of [LocalContentColor]. If [bitmap] has no
 * intrinsic size, this component falls back to the recommended default size. Icon is an
 * opinionated component designed to be used with single-color icons so that they can be tinted
 * correctly for the component they are placed in. For multicolored icons and icons that should not
 * be tinted, use [Color.Unspecified] for [tint]. For generic images that should not be tinted, and
 * do not follow the recommended icon size, use [androidx.compose.foundation.Image] instead. For a
 * clickable icon, see [IconButton].
 *
 * @param bitmap [ImageBitmap] to draw inside this icon
 * @param contentDescription text used by accessibility services to describe what this icon
 *   represents. This should always be provided unless this icon is used for decorative purposes,
 *   and does not represent a meaningful action that a user can take. This text should be localized,
 *   such as by using [stringResource] or similar
 * @param modifier the [Modifier] to be applied to this icon
 * @param tint tint to be applied to [bitmap]. If [Color.Unspecified] is provided, then no tint is
 *   applied.
 */
@Composable
fun Icon(
    bitmap: ImageBitmap,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    tint: Color = LocalContentColor.current,
) {
    val painter = remember(bitmap) { BitmapPainter(bitmap) }
    Icon(
        painter = painter,
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint,
    )
}

/**
 * A [Icon] component with COUI style.
 *
 * Draws [painter] using [tint], with a default value of [LocalContentColor]. If [painter] has no
 * intrinsic size, this component falls back to the recommended default size. Icon is an
 * opinionated component designed to be used with single-color icons so that they can be tinted
 * correctly for the component they are placed in. For multicolored icons and icons that should not
 * be tinted, use [Color.Unspecified] for [tint]. For generic images that should not be tinted, and
 * do not follow the recommended icon size, use [androidx.compose.foundation.Image] instead. For a
 * clickable icon, see [IconButton].
 *
 * @param painter [Painter] to draw inside this icon
 * @param contentDescription text used by accessibility services to describe what this icon
 *   represents. This should always be provided unless this icon is used for decorative purposes,
 *   and does not represent a meaningful action that a user can take. This text should be localized,
 *   such as by using [stringResource] or similar
 * @param modifier the [Modifier] to be applied to this icon
 * @param tint tint to be applied to [painter]. If [Color.Unspecified] is provided, then no tint is
 *   applied.
 */
@Composable
fun Icon(
    painter: Painter,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    tint: Color = LocalContentColor.current,
) {
    val colorFilter =
        remember(tint) { if (tint == Color.Unspecified) null else ColorFilter.tint(tint) }
    val semantics =
        if (contentDescription != null) {
            Modifier.semantics {
                this.contentDescription = contentDescription
                this.role = Role.Image
            }
        } else {
            Modifier
        }
    Box(
        modifier
            .toolingGraphicsLayer()
            .defaultSizeFor(painter)
            .paint(painter, colorFilter = colorFilter, contentScale = ContentScale.Fit)
            .then(semantics),
    )
}

/**
 * A [Icon] component with COUI style.
 *
 * Draws [painter] using a [tint] produced by a [ColorProducer]. The producer is read at draw time,
 * so a tint that changes frequently (animations, scroll-driven values) does not recompose this
 * Icon. If [painter] has no intrinsic size, this component falls back to the recommended default
 * size. Icon is an opinionated component designed to be used with single-color icons so that they
 * can be tinted correctly for the component they are placed in. For multicolored icons and icons
 * that should not be tinted, pass `null` for [tint]. For generic images that should not be tinted,
 * and do not follow the recommended icon size, use [androidx.compose.foundation.Image] instead.
 * For a clickable icon, see [IconButton].
 *
 * @param painter [Painter] to draw inside this icon
 * @param tint tint to be applied to [painter]. If null, then no tint is applied.
 * @param contentDescription text used by accessibility services to describe what this icon
 *   represents. This should always be provided unless this icon is used for decorative purposes,
 *   and does not represent a meaningful action that a user can take. This text should be localized,
 *   such as by using [stringResource] or similar
 * @param modifier the [Modifier] to be applied to this icon
 */
@Composable
fun Icon(
    painter: Painter,
    tint: ColorProducer?,
    contentDescription: String?,
    modifier: Modifier = Modifier,
) {
    Icon(
        painter = painter,
        tint = Color.Unspecified,
        contentDescription = contentDescription,
        modifier =
        modifier.drawWithCache {
            val layer = obtainGraphicsLayer()
            layer.apply {
                record { drawContent() }
                tint?.let { this@apply.colorFilter = ColorFilter.tint(it()) }
            }
            onDrawWithContent { drawLayer(graphicsLayer = layer) }
        },
    )
}

private fun Modifier.defaultSizeFor(painter: Painter) = this.then(
    if (painter.intrinsicSize == Size.Unspecified || painter.intrinsicSize.isInfinite()) {
        DefaultIconSizeModifier
    } else {
        Modifier
    },
)

private fun Size.isInfinite() = width.isInfinite() && height.isInfinite()

// Default icon size, for icons with no intrinsic size information
private val DefaultIconSizeModifier = Modifier.size(24.dp)
